import apiClient from './apiClient';
import { openDB } from 'idb'; // Import idb library
import Logger from '../Services/loggerService';

const DB_NAME = 'WonderBillingDB';
const STORE_NAME = 'billingDataStore';
const DB_VERSION = 1;
const CACHE_DURATION = 60 * 60 * 1000; // 1 hora

// --- IndexedDB Setup ---
async function getDb() {
    return openDB(DB_NAME, DB_VERSION, {
        upgrade(db) {
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: 'id' }); // Use 'id' as the key
            }
        },
    });
}

// --- Helper function to generate a unique ID for storage ---
function generateStorageId(nit, isAdminView) {
    return `billing-${nit}-${isAdminView ? 'admin' : 'user'}`;
}

class BillingService {
    // Controlador de abort compartido para cancelar solicitudes anteriores
    static _currentAbortController = null;

    /**
     * Guarda datos de facturación en IndexedDB
     * @param {Array} invoices - Facturas a guardar (sin reducir)
     * @param {string} nit - NIT del usuario
     * @param {boolean} isAdminView - Si los datos son para vista de administrador
     */
    static async saveBillingData(invoices, nit, isAdminView = false) {
        try {
            if (!Array.isArray(invoices)) {
                console.warn("Datos de facturación inválidos: no es un array.");
                return false;
            }

            // Para administradores con vista "todas las facturas", NO guardar nada
            // Los datasets son demasiado grandes (20MB+) y causan problemas de memoria/espacio
            if (isAdminView) {
                // NO guardar nada, ni en IndexedDB ni en memoria
                // Los datos estarán disponibles solo durante la sesión actual desde la respuesta de la API
                // Retornar true inmediatamente sin intentar guardar nada
                return true;
            }

            const storageId = generateStorageId(nit, isAdminView);
            const dataToStore = {
                id: storageId,
                timestamp: Date.now(),
                data: invoices,
                nit: nit,
                isAdminView: isAdminView
            };

            // Para usuarios normales o datasets pequeños, intentar guardar en IndexedDB
            try {
                // Limpiar datos antiguos del mismo usuario antes de guardar nuevos
                if (!isAdminView) {
                    // Limpiar solo los datos del usuario (no admin)
                    const userStorageId = generateStorageId(nit, false);
                    const db = await getDb();
                    try {
                        await db.delete(STORE_NAME, userStorageId);
                    } catch (deleteError) {
                        // Ignorar si no existen datos antiguos
                    }
                }

                const db = await getDb();
                await db.put(STORE_NAME, dataToStore);
            } catch (storageError) {
                // Detectar específicamente errores de cuota
                const isQuotaError = storageError.name === 'QuotaExceededError' ||
                    storageError.message?.includes('quota') ||
                    storageError.message?.includes('space');

                if (isQuotaError) {
                    console.warn(`⚠️ [BillingService] Espacio insuficiente en IndexedDB. Solo se guardará en memoria.`);
                    // Limpiar IndexedDB y solo mantener en memoria
                    try {
                        const db = await getDb();
                        await db.clear(STORE_NAME);
                    } catch (clearError) {
                        console.error("Error limpiando IndexedDB:", clearError);
                    }
                } else {
                    throw storageError; // Re-lanzar otros errores
                }
            }

            // Actualizar caché en memoria (siempre, incluso si falló IndexedDB)
            BillingService._memoryCache = dataToStore;
            return true;

        } catch (error) {
            console.error("Error guardando datos de facturación:", error);

            // Para administradores, nunca intentar mantener en memoria (datasets demasiado grandes)
            if (isAdminView) {
                console.warn("⚠️ [BillingService] Error guardando datos de administrador. No se mantendrán en memoria (dataset demasiado grande).");
                return true; // Retornar true sin intentar guardar nada
            }

            // Solo para usuarios normales: intentar mantener en memoria como fallback
            const storageId = generateStorageId(nit, isAdminView);
            try {
                BillingService._memoryCache = {
                    id: storageId,
                    timestamp: Date.now(),
                    data: invoices,
                    nit: nit,
                    isAdminView: isAdminView
                };
                console.warn("⚠️ Los datos solo se mantendrán en memoria para esta sesión debido a error de IndexedDB.");
            } catch (memoryError) {
                console.warn("⚠️ No se pudo mantener en memoria tampoco. Los datos estarán disponibles solo durante esta sesión desde la API.");
            }

            // Siempre retornar true para no bloquear el flujo
            return true;
        }
    }

    /**
     * Obtiene datos de facturación de caché (memoria o IndexedDB)
     * @param {string} nit - NIT del usuario actual
     * @param {boolean} checkFreshness - Si debe verificar la frescura de los datos
     * @param {boolean} adminView - Si se desea la vista de administrador
     * @returns {Promise<Object|null>} - Datos de facturación o null si no hay datos válidos
     */
    static async getBillingData(nit, checkFreshness = true, adminView = false) {
        // Para administradores con vista "todas", nunca usar caché
        // Siempre debe cargar desde la API para evitar problemas de espacio
        if (adminView) {
            return null;
        }

        const storageId = generateStorageId(nit, adminView);
        try {
            // PRIMERO: Verificar caché en memoria (solo para usuarios normales)
            if (BillingService._memoryCache && BillingService._memoryCache.id === storageId) {
                const memoryData = BillingService._memoryCache;
                if (!checkFreshness || (Date.now() - memoryData.timestamp <= CACHE_DURATION)) {
                    return {
                        data: memoryData.data,
                        isAdminView: memoryData.isAdminView,
                        timestamp: memoryData.timestamp
                    };
                }
            }

            // SEGUNDO: Intentar obtener de IndexedDB (solo para usuarios normales)
            const db = await getDb();
            const storedData = await db.get(STORE_NAME, storageId);

            if (!storedData) {
                return null;
            }

            // Verificar frescura si se solicita
            if (checkFreshness && Date.now() - storedData.timestamp > CACHE_DURATION) {
                return null;
            }

            // Guardar en memoria para futuras solicitudes
            BillingService._memoryCache = storedData;

            return {
                data: storedData.data,
                isAdminView: storedData.isAdminView,
                timestamp: storedData.timestamp
            };
        } catch (error) {
            console.error(`Error recuperando datos de facturación (ID: ${storageId}):`, error);
            return null;
        }
    }

    /**
     * Limpia los datos de facturación almacenados (memoria e IndexedDB)
     */
    static async clearBillingData() {
        Logger.billing('Limpiando datos de facturación', {
            target: ['memoria', 'IndexedDB', 'localStorage']
        });
        try {
            // Limpiar caché en memoria
            BillingService._memoryCache = null;

            // Limpiar IndexedDB store completo (más simple que buscar por ID específico)
            const db = await getDb();
            await db.clear(STORE_NAME);

            Logger.billing('Datos de facturación limpiados exitosamente');
        } catch (error) {
            Logger.error('Error al limpiar cachés de facturación:', error);
        }
    }

    /**
     * Obtiene datos de facturación (primero de caché, luego de API)
     * @param {string} apiUrl - URL de la API de facturación
     * @param {string} nit - NIT del usuario
     * @param {boolean} forceRefresh - Si debe forzar actualización desde API
     * @param {boolean} isAdmin - Si el usuario es administrador
     * @param {Function} onProgress - Callback para actualizar progreso
     * @returns {Promise<Object>} - Objeto con data, isAdminView, fromCache, etc.
     */
    static async fetchBillingData(apiUrl, nit, forceRefresh = false, isAdmin = false, onProgress = null) {
        // Para administradores, NUNCA usar caché - siempre cargar desde API
        // Los datasets son demasiado grandes (20MB+) y causan problemas de memoria/espacio
        if (isAdmin) {
            // Saltar verificación de caché para administradores
        } else if (!forceRefresh) {
            // Solo verificar caché para usuarios normales
            if (onProgress) onProgress(10, 'Verificando caché...');

            const cachedData = await this.getBillingData(nit, true, false); // false porque no es adminView
            if (cachedData && cachedData.data && cachedData.data.length > 0) {
                if (onProgress) onProgress(100, 'Datos cargados desde caché');
                return {
                    data: cachedData.data,
                    isAdminView: cachedData.isAdminView,
                    timestamp: cachedData.timestamp,
                    fromCache: true
                };
            }
        }

        // Si no hay datos en caché o se fuerza actualización, obtener de API
        if (onProgress) onProgress(20, 'Conectando con servidor...');

        // El token se gestiona en memoria por apiClient

        let requestUrl = apiUrl;
        try {
            const apiUrlWithParams = new URL(apiUrl, window.location.origin);

            if (!isAdmin) {
                apiUrlWithParams.searchParams.append('showAll', 'false');
            } else {
                // Para administradores, agregar showAll=true para obtener todas las facturas
                apiUrlWithParams.searchParams.append('showAll', 'true');
            }

            requestUrl = apiUrlWithParams.toString();
        } catch (error) {
            console.warn("Error construyendo URL, usando original:", error);
        }

        if (onProgress) onProgress(30, 'Conectando con el servidor...');

        // Guardar el controlador anterior para cancelarlo solo si es diferente
        const previousController = BillingService._currentAbortController;

        // Crear nuevo controlador de abort para esta solicitud específica
        const timeoutMs = 300000; // Aumentar a 5 minutos para descargas pesadas de facturación
        const controller = new AbortController();
        let timeoutId = null;
        let progressInterval = null;

        try {
            // Simular progreso incremental mientras se espera la conexión inicial (30% -> 48%)
            let currentSimProgress = 30;
            if (onProgress) {
                progressInterval = setInterval(() => {
                    if (currentSimProgress < 48) {
                        currentSimProgress += 1;
                        onProgress(currentSimProgress, 'Descargando datos...');
                    }
                }, 800);
            }

            timeoutId = setTimeout(() => {
                if (controller && !controller.signal.aborted) {
                    controller.abort();
                }
            }, timeoutMs);

            // Establecer el nuevo controlador como actual
            BillingService._currentAbortController = controller;

            // Cancelar la solicitud anterior solo si existe y es diferente a la actual
            if (previousController && previousController !== controller) {
                try {
                    previousController.abort();
                } catch (e) {
                    // Ignorar errores al cancelar solicitud anterior
                }
            }

            const response = await apiClient.apiFetch(requestUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
                signal: controller.signal
            });

            // Limpiar intervalo de progreso simulado al recibir headers
            if (progressInterval) {
                clearInterval(progressInterval);
                progressInterval = null;
            }

            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }

            // Limpiar controlador solo si esta solicitud fue exitosa y es la actual
            if (BillingService._currentAbortController === controller) {
                BillingService._currentAbortController = null;
            }

            if (!response.ok) {
                throw new Error(`Error al obtener facturas: ${response.status} - ${response.statusText}`);
            }

            if (onProgress) onProgress(50, 'Leyendo datos...');
            const responseData = await response.json();

            if (onProgress) onProgress(70, 'Procesando formato...');

            if (!responseData?.success) {
                throw new Error(responseData?.error || 'Formato de respuesta inválido');
            }

            const invoices = responseData.data || [];
            const isAdminView = isAdmin || responseData.isAdminView || false;

            if (isAdmin && invoices.length === 0) {
                console.warn('⚠️ [BillingService] ADVERTENCIA: Administrador recibió 0 facturas. Esto puede indicar un problema con el backend o la API externa.');
            }

            if (onProgress) onProgress(80, 'Procesando datos...');

            // Para administradores, NO guardar en caché (datasets demasiado grandes)
            // Solo guardar para usuarios normales
            if (!isAdminView) {
                try {
                    await this.saveBillingData(invoices, nit, isAdminView);
                    if (onProgress) onProgress(90, 'Datos guardados en caché');
                } catch (saveError) {
                    console.warn('[BillingService] Error al guardar en almacenamiento:', saveError);
                    // Continuar aunque falle el guardado
                }
            }

            if (onProgress) onProgress(100, 'Completado');

            return {
                data: invoices,
                isAdminView: isAdminView,
                timestamp: Date.now(),
                fromCache: false
            };
        } catch (error) {
            // Limpiar intervalo de progreso si existe
            if (progressInterval) {
                clearInterval(progressInterval);
                progressInterval = null;
            }

            // Limpiar timeout si existe
            if (timeoutId) {
                clearTimeout(timeoutId);
            }

            // Si es un error de abort, verificar si es porque se canceló intencionalmente
            // (hay una nueva solicitud) o si es un timeout/error real
            if (error.name === 'AbortError') {
                // Verificar si el controlador actual es diferente al de esta solicitud
                // Si es diferente, significa que hay una nueva solicitud en curso
                // En ese caso, no lanzar error, solo retornar null silenciosamente
                if (BillingService._currentAbortController && BillingService._currentAbortController !== controller) {
                    // Hay una nueva solicitud en curso, esta fue cancelada intencionalmente
                    // Retornar null para que el componente sepa que debe esperar la nueva solicitud
                    return null;
                }

                // Si el controlador es el mismo, significa que fue un timeout o cancelación inesperada
                // Limpiar el controlador
                if (BillingService._currentAbortController === controller) {
                    BillingService._currentAbortController = null;
                }
                throw new Error('La solicitud excedió el tiempo de espera (5 minutos)');
            }

            // Limpiar controlador en caso de otros errores, solo si es el actual
            if (BillingService._currentAbortController === controller) {
                BillingService._currentAbortController = null;
            }

            console.error("Error en fetchBillingData:", error);
            throw error;
        }
    }

    /**
     * Obtiene el PDF de una factura específica desde la API.
     * @param {string} apiBillPDF - The URL of the backend proxy endpoint.
     * @param {string} numeroFactura - The invoice number.
     * @param {string|null} prefijo - The invoice prefix (optional).
     * @returns {Promise<string>} - A promise that resolves with the base64 encoded PDF string.
     * @throws {Error} - If the fetch fails or the API returns an error.
     */
    static async fetchInvoicePDF(apiBillPDF, numeroFactura, prefijo) {
        // El token se gestiona en memoria por apiClient

        const requestBody = {
            NumeroFactura: numeroFactura,
            ...(prefijo && { Prefijo: prefijo }) // Include prefix only if it exists
        };

        console.log(`[fetchInvoicePDF] Sending POST to ${apiBillPDF} with body:`, requestBody);

        try {
            const response = await apiClient.apiFetch(apiBillPDF, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });

            console.log(`[fetchInvoicePDF] Response status: ${response.status}`);

            if (!response.ok) {
                let errorData;
                let errorMessage = `Error ${response.status}: ${response.statusText}`;
                try {
                    errorData = await response.json();
                    errorMessage = errorData?.error || errorMessage;
                } catch (e) {
                    try {
                        const textError = await response.text();
                        errorMessage = textError || errorMessage;
                        errorData = { error: errorMessage };
                    } catch (textFallbackError) {
                        errorData = { error: errorMessage };
                    }
                }

                const isPdfUnavailableError = response.status === 404 && errorMessage.includes('PDF no disponible');

                if (!isPdfUnavailableError) {
                    console.error(`Error en respuesta de API PDF (${apiBillPDF}): ${errorMessage}`, errorData);
                } else {
                    console.log(`[fetchInvoicePDF] Info: PDF no disponible detectado para ${prefijo || ''}${numeroFactura}. Backend response:`, errorData);
                }

                throw new Error(errorMessage);
            }

            const data = await response.json();

            if (!data.success || !data.pdfBase64) {
                const errorMessage = data.error || 'La API no devolvió el PDF esperado.';
                console.error(`[fetchInvoicePDF] API indicated failure: ${errorMessage}`, data);
                throw new Error(errorMessage);
            }

            console.log(`[fetchInvoicePDF] PDF base64 received successfully for ${prefijo || ''}${numeroFactura}`);
            return data.pdfBase64;

        } catch (error) {
            const isPdfUnavailableError = error.message && error.message.includes('PDF no disponible');

            if (!isPdfUnavailableError) {
                console.error(`Error en fetchInvoicePDF para ${prefijo || ''}${numeroFactura}:`, error);
            }

            throw error;
        }
    }

    /**
     * Sincroniza WonderPoints y Facturación usando Server-Sent Events (SSE)
     * @param {Function} onProgress - Callback para progreso: (progress, message, status) => {}
     * @returns {Promise<Object>} - Resultado final de la sincronización
     */
    static async syncWonderPointsSSE(onProgress = null) {
        const syncUrl = `${import.meta.env.VITE_API_URL}/wonderpoints/sync`;
        
        try {
            if (onProgress) onProgress(0, 'Iniciando conexión...', 'loading');

            const response = await apiClient.apiFetch(syncUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'text/event-stream'
                }
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `Error en la conexión: ${response.status}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let finalResult = null;

            while (true) {
                const { value, done } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value);
                const lines = chunk.split('\n');

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.substring(6));
                            
                            // Mapear eventos de SSE a los estados del loader
                            if (data.type === 'progress') {
                                if (onProgress) onProgress(data.progress, data.message || 'Procesando...', 'loading');
                            } else if (data.type === 'status') {
                                if (onProgress) onProgress(data.progress, data.message || 'Actualizando...', 'loading');
                            } else if (data.type === 'complete') {
                                if (onProgress) onProgress(100, data.message || 'Sincronización completada', 'completed');
                                finalResult = data;
                            } else if (data.type === 'error') {
                                throw new Error(data.error || 'Error en la sincronización remota');
                            }
                        } catch (e) {
                            if (e instanceof SyntaxError) continue;
                            throw e;
                        }
                    }
                }
            }

            return finalResult || { success: true };
        } catch (error) {
            console.error('Error en syncWonderPointsSSE:', error);
            if (onProgress) onProgress(null, error.message, 'error');
            throw error;
        }
    }

    // Variable estática para caché en memoria
    static _memoryCache = null;
}


export default BillingService;
